import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '../components/Sidebar';
import { LoadingSpinner } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { pricingAPI } from '../utils/api';
import CatalogControlsPanel from '../components/catalog/CatalogControlsPanel';
import BuyPriceBooksPanel from '../components/catalog/BuyPriceBooksPanel';
import RentalDefaultsPanel from '../components/catalog/RentalDefaultsPanel';
import VoltideSKUSheet from '../components/catalog/VoltideSKUSheet';
import {
  Factory, Plus, Edit, Trash2, Zap, Battery, Upload, Search,
  CheckCircle, AlertTriangle, XCircle, HelpCircle, Cpu, Boxes,
  Grid3X3, ToggleLeft, ToggleRight, FileSpreadsheet, ArrowUpCircle,
  ChevronDown, ChevronRight, Globe, BookOpen, Settings2, LayoutList
} from 'lucide-react';
import { toast } from 'sonner';

const TAB_GROUPS = [
  {
    label: 'Costing',
    tabs: [
      { id: 'pcs', label: 'PCS Master', icon: Zap },
      { id: 'battery', label: 'Battery Master', icon: Battery },
      { id: 'systems', label: 'System SKUs', icon: Boxes },
      { id: 'matrix', label: 'Cost Matrix', icon: Grid3X3 },
    ],
  },
  {
    label: 'Catalog',
    tabs: [
      { id: 'sheet', label: 'Voltide SKU Sheet', icon: LayoutList },
      { id: 'rentaldefaults', label: 'Rental Defaults', icon: Settings2 },
    ],
  },
];

const ALL_TABS = TAB_GROUPS.flatMap(g => g.tabs);

const COMPAT_BADGE = {
  YES: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  NO: { color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
  POSSIBLE: { color: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle },
  ENGINEERING_REVIEW: { color: 'bg-blue-50 text-blue-700 border-blue-200', icon: HelpCircle },
};

export default function PricingMasterPage() {
  const [activeTab, setActiveTab] = useState('pcs');
  const [vendors, setVendors] = useState([]);
  const [pcsList, setPcsList] = useState([]);
  const [batteries, setBatteries] = useState([]);
  const [systems, setSystems] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);

  const isCostingTab = ['pcs', 'battery', 'systems', 'matrix'].includes(activeTab);
  const isSheetTab = activeTab === 'sheet';
  const isRentalDefTab = activeTab === 'rentaldefaults';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, pRes, bRes, sRes, mRes] = await Promise.all([
        pricingAPI.getVendors(), pricingAPI.getPCS(), pricingAPI.getBatteries(),
        pricingAPI.getSystems(), pricingAPI.getMatrix(),
      ]);
      setVendors(vRes.data); setPcsList(pRes.data); setBatteries(bRes.data);
      setSystems(sRes.data); setMatrix(mRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const counts = { pcs: pcsList.length, battery: batteries.length, systems: systems.length, matrix: matrix.length };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-slate-900" data-testid="pricing-master-title">Pricing & Catalog</h1>
              <p className="text-sm text-slate-500 mt-1">Costing, catalog controls, buy & rental pricing</p>
            </div>
            {isCostingTab && (
              <Button size="sm" className="bg-slate-900" onClick={() => setShowImport(true)} data-testid="import-btn">
                <Upload className="w-4 h-4 mr-2" /> Import XLSX
              </Button>
            )}
          </div>

          {/* Sub-navigation with groups */}
          <div className="bg-white border border-slate-200 rounded-sm p-1.5 mb-6" data-testid="pricing-tabs">
            <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
              {TAB_GROUPS.map((group, gi) => (
                <React.Fragment key={group.label}>
                  {gi > 0 && <div className="w-px h-7 bg-slate-200 mx-1.5 hidden sm:block" />}
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 hidden sm:inline">{group.label}</span>
                  {group.tabs.map(tab => {
                    const Icon = tab.icon;
                    const active = activeTab === tab.id;
                    const count = counts[tab.id];
                    return (
                      <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
                        className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                        <Icon className="w-3.5 h-3.5" /><span>{tab.label}</span>
                        {count !== undefined && (
                          <span className={`text-[10px] ml-0.5 px-1.5 py-0.5 rounded ${active ? 'bg-white/20' : 'bg-slate-100'}`}>{count}</span>
                        )}
                      </button>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Content panels */}
          {isCostingTab && loading ? <LoadingSpinner /> : (
            <>
              {activeTab === 'pcs' && <PCSPanel items={pcsList} vendors={vendors} onRefresh={fetchAll} />}
              {activeTab === 'battery' && <BatteryPanel items={batteries} vendors={vendors} onRefresh={fetchAll} />}
              {activeTab === 'systems' && <SystemsPanel items={systems} vendors={vendors} pcsList={pcsList} batteries={batteries} onRefresh={fetchAll} />}
              {activeTab === 'matrix' && <MatrixPanel items={matrix} vendors={vendors} pcsList={pcsList} batteries={batteries} onRefresh={fetchAll} />}
              {activeTab === 'sheet' && <VoltideSKUSheet />}
              {activeTab === 'rentaldefaults' && <RentalDefaultsPanel />}
            </>
          )}

          {showImport && <ImportDialog onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); fetchAll(); }} />}
        </div>
      </div>
    </div>
  );
}

/* ==================== PCS PANEL ==================== */

function PCSPanel({ items, vendors, onRefresh }) {
  const [search, setSearch] = useState('');
  const filtered = items.filter(p => p.pcs_sku.toLowerCase().includes(search.toLowerCase()) || (p.vendor_name || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="relative w-72">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <Input placeholder="Search PCS SKU or vendor..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="pcs-search" />
        </div>
        <p className="text-xs text-slate-400">{filtered.length} PCS unit(s)</p>
      </div>
      {filtered.length === 0 ? (
        <EmptyState icon={Zap} text="No PCS units found" sub="Import an XLSX file to add PCS units" />
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="pcs-table">
            <thead><tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">PCS SKU</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Family</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Vendor</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Rated Power</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Min Unit kW</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">DC Input</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Efficiency</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">PF / THD</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map(p => (
                <tr key={p.pcs_id} className="hover:bg-slate-50" data-testid={`pcs-row-${p.pcs_id}`}>
                  <td className="px-4 py-3"><span className="font-mono font-medium text-slate-900">{p.pcs_sku}</span></td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{p.pcs_family || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{p.vendor_name}</td>
                  <td className="px-4 py-3 text-right font-mono font-medium text-slate-900">{p.rated_power_kw ? `${p.rated_power_kw} kW` : '—'}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-indigo-600">{p.min_pcs_unit_kw ? `${p.min_pcs_unit_kw} kW` : <span className="text-slate-300">= rated</span>}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{p.dc_input_range_v || '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{p.efficiency_pct || '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{p.power_factor || '—'} / {p.thd_pct || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ==================== BATTERY PANEL ==================== */

function BatteryPanel({ items, vendors, onRefresh }) {
  const [search, setSearch] = useState('');
  const filtered = items.filter(b => b.battery_sku.toLowerCase().includes(search.toLowerCase()) || (b.vendor_name || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="relative w-72">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <Input placeholder="Search Battery SKU or vendor..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="battery-search" />
        </div>
        <p className="text-xs text-slate-400">{filtered.length} battery unit(s)</p>
      </div>
      {filtered.length === 0 ? (
        <EmptyState icon={Battery} text="No battery units found" sub="Import an XLSX file to add battery units" />
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="battery-table">
            <thead><tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Battery SKU</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Family</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Vendor</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Capacity</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Min Block kWh</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Chemistry</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">DC Range</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Cooling</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">IP</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map(b => (
                <tr key={b.battery_id} className="hover:bg-slate-50" data-testid={`battery-row-${b.battery_id}`}>
                  <td className="px-4 py-3"><span className="font-mono font-medium text-slate-900">{b.battery_sku}</span></td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{b.battery_family || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{b.vendor_name}</td>
                  <td className="px-4 py-3 text-right font-mono font-medium text-slate-900">{b.rated_energy_kwh ? `${b.rated_energy_kwh} kWh` : '—'}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-indigo-600">{b.min_battery_block_kwh ? `${b.min_battery_block_kwh} kWh` : <span className="text-slate-300">= rated</span>}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{b.chemistry || '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{b.dc_voltage_range_v || '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{b.cooling_technology || '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{b.ingress_protection || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ==================== SYSTEMS PANEL ==================== */

function SystemsPanel({ items, vendors, pcsList, batteries, onRefresh }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-xs text-slate-400">{items.length} system SKU(s)</p>
      </div>
      {items.length === 0 ? (
        <EmptyState icon={Boxes} text="No system SKUs found" sub="Import an XLSX file to add system configurations" />
      ) : (
        <div className="space-y-3">
          {items.map(s => (
            <div key={s.system_id} className="bg-white border border-slate-200 rounded-sm shadow-sm p-4" data-testid={`system-card-${s.system_id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 bg-violet-50 rounded-sm flex items-center justify-center">
                    <Boxes className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-mono font-semibold text-slate-900">{s.system_sku}</p>
                    <p className="text-xs text-slate-500">{s.vendor_name} · {s.system_series || s.application || 'General'}</p>
                  </div>
                </div>
                <span className="text-xs bg-slate-100 px-2 py-1 rounded font-medium">{s.application || '—'}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                <Spec label="PCS" value={s.pcs_sku} icon={Zap} />
                <Spec label="Battery" value={s.battery_sku} icon={Battery} />
                <Spec label="Power" value={s.rated_power_kw ? `${s.rated_power_kw} kW` : '—'} />
                <Spec label="Energy" value={s.battery_energy_kwh ? `${s.battery_energy_kwh} kWh` : '—'} />
                <Spec label="Container" value={s.container_or_enclosure || '—'} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ==================== COST MATRIX PANEL ==================== */

function MatrixPanel({ items, vendors, pcsList, batteries, onRefresh }) {
  const [filterPower, setFilterPower] = useState('');
  const [filterKwh, setFilterKwh] = useState('');
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showInactive, setShowInactive] = useState(false);

  const activeItems = items.filter(m => m.is_active !== false);
  const inactiveItems = items.filter(m => m.is_active === false);
  const displayItems = showInactive ? inactiveItems : activeItems;

  const filtered = displayItems.filter(m => {
    if (filterPower && m.pcs_rated_power_kw && m.pcs_rated_power_kw < Number(filterPower)) return false;
    if (filterKwh && m.battery_rated_energy_kwh && m.battery_rated_energy_kwh < Number(filterKwh)) return false;
    return true;
  });

  const handleToggle = async (id) => {
    try { await pricingAPI.toggleMatrix(id); toast.success('Status toggled'); onRefresh(); }
    catch { toast.error('Failed to toggle'); }
  };

  const startEdit = (m) => {
    setEditId(m.matrix_id);
    setEditForm({
      pcs_price_per_kw_INR: m.pcs_price_per_kw_INR || '',
      battery_price_per_kwh_INR: m.battery_price_per_kwh_INR || '',
      container_BOS_fixed_INR: m.container_BOS_fixed_INR || '',
      integration_commissioning_INR: m.integration_commissioning_INR || '',
      amc_annual_INR: m.amc_annual_INR || '',
      warranty_months: m.warranty_months || '',
      total_bess_cost_INR: m.total_bess_cost_INR || '',
    });
  };

  const saveEdit = async (m) => {
    try {
      await pricingAPI.updateMatrix(m.matrix_id, {
        ...m,
        pcs_price_per_kw_INR: Number(editForm.pcs_price_per_kw_INR) || null,
        battery_price_per_kwh_INR: Number(editForm.battery_price_per_kwh_INR) || null,
        container_BOS_fixed_INR: Number(editForm.container_BOS_fixed_INR) || null,
        integration_commissioning_INR: Number(editForm.integration_commissioning_INR) || null,
        amc_annual_INR: Number(editForm.amc_annual_INR) || null,
        warranty_months: Number(editForm.warranty_months) || null,
        total_bess_cost_INR: Number(editForm.total_bess_cost_INR) || null,
      });
      toast.success('Pricing updated');
      setEditId(null);
      onRefresh();
    } catch { toast.error('Failed to update'); }
  };

  const fmt = (n) => n ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

  const getDisplayTotal = (m) => {
    if (m.total_bess_cost_INR) return m.total_bess_cost_INR;
    const pcsKw = m.pcs_rated_power_kw || 0;
    const batKwh = m.battery_rated_energy_kwh || 0;
    const pcsCost = (m.pcs_price_per_kw_INR || 0) * pcsKw;
    const batCost = (m.battery_price_per_kwh_INR || 0) * batKwh;
    const bos = m.container_BOS_fixed_INR || 0;
    const integ = m.integration_commissioning_INR || 0;
    const total = pcsCost + batCost + bos + integ;
    return total > 0 ? total : null;
  };

  return (
    <div>
      <div className="flex items-center space-x-1 mb-4 bg-slate-100 rounded-sm p-0.5 w-fit" data-testid="matrix-status-tabs">
        <button
          className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${!showInactive ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          onClick={() => setShowInactive(false)} data-testid="matrix-tab-active">
          Active ({activeItems.length})
        </button>
        <button
          className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${showInactive ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          onClick={() => setShowInactive(true)} data-testid="matrix-tab-inactive">
          Inactive ({inactiveItems.length})
        </button>
      </div>

      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Zap className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
            <Input type="number" placeholder="Min PCS kW..." value={filterPower} onChange={e => setFilterPower(e.target.value)} className="pl-9 w-44" data-testid="matrix-filter-kw" />
          </div>
          <div className="relative">
            <Battery className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
            <Input type="number" placeholder="Min Battery kWh..." value={filterKwh} onChange={e => setFilterKwh(e.target.value)} className="pl-9 w-44" data-testid="matrix-filter-kwh" />
          </div>
        </div>
        <p className="text-xs text-slate-400">{filtered.length} combo(s){showInactive ? ' (inactive)' : ''}</p>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Grid3X3} text="No pricing matrix entries" sub="Import an XLSX file to populate the pricing matrix" />
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="matrix-table">
            <thead><tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">PCS SKU</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Battery SKU</th>
              <th className="text-center px-3 py-3 text-xs font-semibold text-slate-500 uppercase">Compat</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">PCS ₹/kW</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Bat ₹/kWh</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">BOS Fixed</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Integration</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">AMC/yr</th>
              <th className="text-center px-3 py-3 text-xs font-semibold text-slate-500 uppercase">Warranty</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-emerald-700 uppercase whitespace-nowrap bg-emerald-50/50">Total Cost (₹)</th>
              <th className="text-center px-3 py-3 text-xs font-semibold text-slate-500 uppercase">Active</th>
              <th className="text-right px-3 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map(m => {
                const compat = COMPAT_BADGE[m.compatibility_status] || COMPAT_BADGE.POSSIBLE;
                const CompatIcon = compat.icon;
                const isEditing = editId === m.matrix_id;
                const displayTotal = getDisplayTotal(m);

                return (
                  <tr key={m.matrix_id} className={`hover:bg-slate-50 ${!m.is_active ? 'opacity-50' : ''}`} data-testid={`matrix-row-${m.matrix_id}`}>
                    <td className="px-3 py-3">
                      <span className="font-mono text-xs font-medium text-slate-900">{m.pcs_sku}</span>
                      {m.pcs_rated_power_kw && <span className="text-[10px] text-slate-400 ml-1">({m.pcs_rated_power_kw}kW)</span>}
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-mono text-xs font-medium text-slate-900">{m.battery_sku}</span>
                      {m.battery_rated_energy_kwh && <span className="text-[10px] text-slate-400 ml-1">({m.battery_rated_energy_kwh}kWh)</span>}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded border ${compat.color}`} title={m.compatibility_reason}>
                        <CompatIcon className="w-3 h-3 mr-1" />{m.compatibility_status}
                      </span>
                    </td>
                    {isEditing ? (
                      <>
                        <td className="px-2 py-2"><Input type="number" className="h-8 text-xs w-24" value={editForm.pcs_price_per_kw_INR} onChange={e => setEditForm({...editForm, pcs_price_per_kw_INR: e.target.value})} placeholder="₹/kW" data-testid="edit-pcs-price" /></td>
                        <td className="px-2 py-2"><Input type="number" className="h-8 text-xs w-24" value={editForm.battery_price_per_kwh_INR} onChange={e => setEditForm({...editForm, battery_price_per_kwh_INR: e.target.value})} placeholder="₹/kWh" data-testid="edit-bat-price" /></td>
                        <td className="px-2 py-2"><Input type="number" className="h-8 text-xs w-24" value={editForm.container_BOS_fixed_INR} onChange={e => setEditForm({...editForm, container_BOS_fixed_INR: e.target.value})} placeholder="₹ fixed" /></td>
                        <td className="px-2 py-2"><Input type="number" className="h-8 text-xs w-24" value={editForm.integration_commissioning_INR} onChange={e => setEditForm({...editForm, integration_commissioning_INR: e.target.value})} placeholder="₹ fixed" /></td>
                        <td className="px-2 py-2"><Input type="number" className="h-8 text-xs w-24" value={editForm.amc_annual_INR} onChange={e => setEditForm({...editForm, amc_annual_INR: e.target.value})} placeholder="₹/yr" /></td>
                        <td className="px-2 py-2"><Input type="number" className="h-8 text-xs w-16" value={editForm.warranty_months} onChange={e => setEditForm({...editForm, warranty_months: e.target.value})} placeholder="mo" /></td>
                        <td className="px-2 py-2 bg-emerald-50/30"><Input type="number" className="h-8 text-xs w-28 border-emerald-300 font-bold" value={editForm.total_bess_cost_INR} onChange={e => setEditForm({...editForm, total_bess_cost_INR: e.target.value})} placeholder="Total ₹" data-testid="edit-total-cost" /></td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-3 text-right text-xs font-mono font-medium text-slate-900">{fmt(m.pcs_price_per_kw_INR)}</td>
                        <td className="px-3 py-3 text-right text-xs font-mono font-medium text-slate-900">{fmt(m.battery_price_per_kwh_INR)}</td>
                        <td className="px-3 py-3 text-right text-xs font-mono text-slate-600">{fmt(m.container_BOS_fixed_INR)}</td>
                        <td className="px-3 py-3 text-right text-xs font-mono text-slate-600">{fmt(m.integration_commissioning_INR)}</td>
                        <td className="px-3 py-3 text-right text-xs font-mono text-slate-600">{fmt(m.amc_annual_INR)}</td>
                        <td className="px-3 py-3 text-center text-xs font-mono text-slate-600">{m.warranty_months ? `${m.warranty_months}mo` : '—'}</td>
                        <td className="px-3 py-3 text-right bg-emerald-50/30" data-testid={`total-cost-${m.matrix_id}`}>
                          {displayTotal ? (
                            <div>
                              <span className="text-sm font-mono font-medium text-emerald-700">{fmt(displayTotal)}</span>
                              {!m.total_bess_cost_INR && displayTotal > 0 && (
                                <p className="text-[9px] text-slate-400 italic">computed</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                      </>
                    )}
                    <td className="px-3 py-3 text-center">
                      <button onClick={() => handleToggle(m.matrix_id)} data-testid={`toggle-${m.matrix_id}`} title={m.is_active ? 'Deactivate' : 'Activate'}>
                        {m.is_active ? <ToggleRight className="w-5 h-5 text-emerald-600" /> : <ToggleLeft className="w-5 h-5 text-slate-400" />}
                      </button>
                    </td>
                    <td className="px-3 py-3 text-right">
                      {isEditing ? (
                        <div className="flex space-x-1 justify-end">
                          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => saveEdit(m)} data-testid="save-edit-btn">Save</Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditId(null)}>Cancel</Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => startEdit(m)} data-testid={`edit-matrix-${m.matrix_id}`}>
                          <Edit className="w-3 h-3 mr-1" /> Edit
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ==================== IMPORT DIALOG ==================== */

function ImportDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (s) => setExpandedSections(prev => ({...prev, [s]: !prev[s]}));

  const handlePreview = async () => {
    if (!file) return;
    setLoadingPreview(true);
    try {
      const res = await pricingAPI.importPreview(file);
      setPreview(res.data);
    } catch (e) { toast.error('Failed to parse file'); }
    finally { setLoadingPreview(false); }
  };

  const handleApprove = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const res = await pricingAPI.importApprove(file);
      toast.success(`Import complete! Created: ${res.data.stats.pcs_created} PCS, ${res.data.stats.batteries_created} batteries, ${res.data.stats.systems_created} systems, ${res.data.stats.matrix_created} matrix rows`);
      onDone();
    } catch (e) { toast.error('Import failed'); }
    finally { setImporting(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Import Vendor Product Master</DialogTitle></DialogHeader>

        {!preview ? (
          <div className="space-y-4 mt-2">
            <p className="text-sm text-slate-500">Upload an XLSX file with sheets: <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">pcs</code>, <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">battery</code>, <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">system</code>, <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">compatibility</code></p>
            <div className="border-2 border-dashed border-slate-200 rounded-sm p-8 text-center">
              <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <input type="file" accept=".xlsx,.xls" onChange={e => setFile(e.target.files[0])} className="text-sm" data-testid="import-file-input" />
              {file && <p className="text-xs text-slate-500 mt-2">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>}
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-slate-900" disabled={!file || loadingPreview} onClick={handlePreview} data-testid="preview-import-btn">
                {loadingPreview ? 'Parsing...' : 'Preview Import'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-4" data-testid="import-summary">
              <h4 className="font-semibold text-sm text-slate-900 mb-3">Import Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SumBox label="Vendors" newCount={preview.summary.vendors_new} existCount={preview.summary.vendors_existing} />
                <SumBox label="PCS Units" newCount={preview.summary.pcs_new} dupCount={preview.summary.pcs_duplicate} />
                <SumBox label="Batteries" newCount={preview.summary.batteries_new} dupCount={preview.summary.batteries_duplicate} />
                <SumBox label="Systems" newCount={preview.summary.systems_new} />
                <div className="col-span-2 md:col-span-4 bg-white border border-slate-100 rounded-sm px-3 py-2">
                  <span className="text-xs text-slate-500">Cost Matrix rows: </span>
                  <span className="text-sm font-bold text-slate-900">{preview.summary.matrix_rows}</span>
                </div>
              </div>
            </div>

            <CollapsibleList title={`PCS Units (${preview.pcs.length})`} items={preview.pcs} expanded={expandedSections.pcs} toggle={() => toggleSection('pcs')}
              renderItem={p => <span className="font-mono text-xs">{p.pcs_sku} — {p.rated_power_kw || '?'}kW {p.duplicate ? <span className="text-amber-600 font-bold ml-2">(exists, skip)</span> : <span className="text-emerald-600 ml-2">(new)</span>}</span>} />
            <CollapsibleList title={`Batteries (${preview.batteries.length})`} items={preview.batteries} expanded={expandedSections.bat} toggle={() => toggleSection('bat')}
              renderItem={b => <span className="font-mono text-xs">{b.battery_sku} — {b.rated_energy_kwh || '?'}kWh {b.duplicate ? <span className="text-amber-600 font-bold ml-2">(exists, skip)</span> : <span className="text-emerald-600 ml-2">(new)</span>}</span>} />
            <CollapsibleList title={`Systems (${preview.systems.length})`} items={preview.systems} expanded={expandedSections.sys} toggle={() => toggleSection('sys')}
              renderItem={s => <span className="font-mono text-xs">{s.system_sku} — PCS:{s.pcs_sku} + Bat:{s.battery_sku} {s.duplicate ? <span className="text-amber-600 font-bold ml-2">(exists, skip)</span> : <span className="text-emerald-600 ml-2">(new)</span>}</span>} />
            <CollapsibleList title={`Cost Matrix (${preview.matrix.length})`} items={preview.matrix} expanded={expandedSections.matrix} toggle={() => toggleSection('matrix')}
              renderItem={m => <span className="font-mono text-xs">{m.pcs_sku} x {m.battery_sku} — {m.compatibility_status} {m.brochure_listed_combo === 'YES' ? '(brochure)' : ''}</span>} />

            <div className="flex justify-end space-x-3 pt-3 border-t">
              <Button variant="outline" onClick={() => setPreview(null)}>Back</Button>
              <Button className="bg-slate-900" disabled={importing} onClick={handleApprove} data-testid="approve-import-btn">
                <ArrowUpCircle className="w-4 h-4 mr-2" />{importing ? 'Importing...' : 'Approve Import'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ==================== SHARED COMPONENTS ==================== */

function EmptyState({ icon: Icon, text, sub }) {
  return (
    <div className="text-center py-16 bg-white border border-slate-200 rounded-sm">
      <Icon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
      <p className="text-sm text-slate-500">{text}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

function Spec({ label, value, icon: Icon }) {
  return (
    <div>
      <p className="text-[10px] text-slate-400 uppercase mb-0.5">{label}</p>
      <div className="flex items-center space-x-1">
        {Icon && <Icon className="w-3 h-3 text-slate-400" />}
        <span className="font-mono text-xs font-medium text-slate-800 truncate">{value || '—'}</span>
      </div>
    </div>
  );
}

function SumBox({ label, newCount = 0, existCount, dupCount }) {
  return (
    <div className="bg-white border border-slate-100 rounded-sm px-3 py-2">
      <p className="text-xs text-slate-500">{label}</p>
      <div className="flex items-baseline space-x-2">
        <span className="text-sm font-bold text-emerald-700">+{newCount}</span>
        {existCount !== undefined && <span className="text-[10px] text-slate-400">{existCount} existing</span>}
        {dupCount !== undefined && dupCount > 0 && <span className="text-[10px] text-amber-600">{dupCount} skipped</span>}
      </div>
    </div>
  );
}

function CollapsibleList({ title, items, expanded, toggle, renderItem }) {
  return (
    <div className="border border-slate-200 rounded-sm">
      <button onClick={toggle} className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-sm font-medium text-slate-700">
        {title}
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {expanded && (
        <div className="px-4 py-2 space-y-1 max-h-40 overflow-y-auto">
          {items.map((item, i) => <div key={(typeof item === 'string' ? item : (item.id || item.sku_code || item.name)) || i} className="py-1 border-b border-slate-50 last:border-0">{renderItem(item)}</div>)}
        </div>
      )}
    </div>
  );
}
